import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const ZanzibarSafariBlue = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">5 DAYS ZANZIBAR SAFARI BLUE & BEACH HOLIDAY</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          5 Days Zanzibar Safari Blue & Beach Holiday
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Experience the ultimate tropical escape with this <strong>5 Days Zanzibar Safari Blue & Beach Holiday</strong>. Dive into the vibrant marine life with a Safari Blue sea adventure, swim with dolphins, explore the famous Spice Tour, and relax on Zanzibar’s pristine white sand beaches. This tour blends adventure and leisure for an unforgettable getaway.
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 5 Days Zanzibar Safari Blue & Beach Holiday
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival in Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Arrive at Zanzibar Airport, where you will be warmly met and transferred to a charming hotel in Stone Town. Check in, enjoy welcome drinks, and spend the evening relaxing by the pool or exploring the historic surroundings before a delightful dinner.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Shaba Lodge & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Safari Blue Sea Adventure</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, transfer to Fumba Fishing Village for a full-day Safari Blue Sea Adventure. Sail by traditional dhow to Kwale Island, enjoying snorkeling, swimming, and sunbathing. Savor a delicious seafood lunch on the ocean before returning to your beach resort for dinner and a restful evening.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Swim with Dolphins & Spice Tour</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Start your day with breakfast, then head to Kizimkazi Fishing Village for a dolphin-swimming adventure in the vibrant waters of Menai Bay. After lunch by the beach, proceed to a Spice Tour to explore Zanzibar’s botanical treasures, including cloves, cinnamon, cardamom, and tropical fruits like mangoes and jackfruit. Continue to Jozani Forest to observe the rare red colobus monkeys before transferring to a beach resort in Nungwi or Pongwe for dinner and an overnight stay.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 4: Free & Beach Leisure</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Enjoy a leisurely day at your beach resort after breakfast. Relax on the white sand beaches or opt for activities like Mnemba Island snorkeling, Stone Town tours, or Swahili cultural experiences (available upon request). End the day with a delicious dinner at the resort.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 5: Departure from Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, transfer to Stone Town for a final shopping experience at the bazaar and souvenir shops on Mizingani Street, exploring Zanzibar’s charming narrow streets. Afterward, transfer to Zanzibar Airport for your scheduled flight home.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Airport transfers in Zanzibar
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated (half board)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Safari Blue Sea Adventure, Spice Tour, and dolphin-swimming excursion
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Jozani Forest tour
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International flights and visa fees
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities (e.g., Mnemba snorkeling, Stone Town tour)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic beverages
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">5 Days Zanzibar Safari Blue & Beach Holiday</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 5 Days, 4 Nights
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Zanzibar
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Airport pickup - Return to airport
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZanzibarSafariBlue;